import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import { showMessage } from 'app/store/fuse/messageSlice';
import { logoutUser, setUser } from 'app/store/userSlice';
import { setNavigation } from 'app/store/fuse/navigationSlice';
import NavigationUtil from 'app/nav/NavigationUtil';
import { NETWORK_RESET_MESSAGE, shouldRetryAutoLogin } from 'app/utils/apiError';
import axios from 'axios';
import { confirmUnpricedLines, installUnpricedLinesWarning } from 'app/shared-components/unpricedLinesWarning';
import jwtService from './services/jwtService';
import { DISABLED_LOGIN_NOTICE, isDisabledLoginError, takeSignOutNotice } from './disabledLogin';

const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(undefined);
  const [waitAuthCheck, setWaitAuthCheck] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    jwtService.on('onAutoLogin', () => {
      /**
       * Sign in and retrieve user data with stored token.
       * On a brief API outage keep the splash and retry — sending everyone to
       * sign-in is what caused the prod login stampede (PGSS-5337).
       */
      const trySignIn = (attempt = 0) => {
        jwtService
          .signInWithToken()
          .then((user) => {
            if (cancelled) {
              return;
            }
            success(user, null);
            const navUtil = new NavigationUtil();
            navUtil.loadMenus(user?.securityContext?.menus);
            const navigationArray = navUtil.getNavigation(true);
            dispatch(setNavigation(navigationArray));
          })
          .catch((error) => {
            if (cancelled) {
              return;
            }
            if (shouldRetryAutoLogin(error)) {
              const delayMs = Math.min(2000 * 2 ** Math.min(attempt, 3), 15000)
                + Math.floor(Math.random() * 500);
              retryTimer = window.setTimeout(() => trySignIn(attempt + 1), delayMs);
              return;
            }
            // PGSS-5715: a disabled login's message names the account; say it plainly instead.
            if (isDisabledLoginError(error)) {
              pass(DISABLED_LOGIN_NOTICE);
              return;
            }
            const message = error?.message;
            pass(message === NETWORK_RESET_MESSAGE ? null : message);
          });
      };
      trySignIn(0);
    });

    jwtService.on('onLogin', (user) => {
      success(user, null);
    });

    jwtService.on('onLogout', () => {
      pass('Signed out');

      dispatch(logoutUser());
    });

    jwtService.on('onAutoLogout', (message) => {
      pass(message);

      dispatch(logoutUser());
    });

    jwtService.on('onNoAccessToken', () => {
      // PGSS-5715: a notice parked before a sign-out reload (e.g. a login disabled mid-session).
      pass(takeSignOutNotice());
    });

    jwtService.init();
    // PGSS-5702: after jwtService's handler, so it sees the normalized error; one handler for every screen.
    installUnpricedLinesWarning(axios, (items) => confirmUnpricedLines(dispatch, items));

    function success(user, message) {
      if (message) {
        dispatch(showMessage({ message }));
      }

      Promise.all([
        dispatch(setUser(user)),
        // You can receive data in here before app initialization
      ]).then((values) => {
        setWaitAuthCheck(false);
        setIsAuthenticated(true);
      });
    }

    function pass(message) {
      if (message) {
        dispatch(showMessage({ message }));
      }

      setWaitAuthCheck(false);
      setIsAuthenticated(false);
    }

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [dispatch]);

  return waitAuthCheck ? (
    <FuseSplashScreen />
  ) : (
    <AuthContext.Provider value={{ isAuthenticated }}>{children}</AuthContext.Provider>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
